import { apiService } from "./apiService";

export type UserRole = "admin" | "user" | "superadmin";

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  companyId: string;
  isActive: boolean;
  mustChangePassword: boolean;
  password: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Company {
  id: string;
  name: string;
  cif: string;
  address: string;
  city: string;
  postalCode: string;
  email: string;
  phone: string;
  website: string;
  isActive: boolean;
  invoicePrefix: string;
  currentYear: number;
  lastInvoiceNumber: number;
  fiscalYearStart: number;
  logo: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Session {
  isAuthenticated: boolean;
  user: User;
  company: Company;
  token: string;
  expiresAt: Date;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  code?: string;
  session?: Session;
  user?: User;
  company?: Company;
}

export interface PasswordValidation {
  isValid: boolean;
  message: string;
}


export class AuthService {
  private static STORAGE_KEY = 'factuapp_session';
  private static currentSession: Session | null = null;
  
  // Usuarios predefinidos
  private static users: User[] = [
    {
      id: 'superadmin_001',
      email: 'saikoner@gmail.com',
      name: 'Saikoner Admin',
      role: 'superadmin',
      companyId: 'company_default',
      isActive: true,
      mustChangePassword: false,
      password: 'admin123!',
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];

  private static companies: Company[] = [
    {
      id: 'company_default',
      name: 'Entik Media',
      cif: 'B00000000',
      address: 'Dirección de la empresa',
      city: 'Ciudad',
      postalCode: '00000',
      email: 'saikoner@gmail.com',
      phone: '+34 000 000 000',
      website: 'https://entikmedia.com',
      isActive: true,
      invoicePrefix: 'FAC',
      currentYear: new Date().getFullYear(),
      lastInvoiceNumber: 0,
      fiscalYearStart: 1,
      logo:
        'https://mlty5i8mxpr9.i.optimole.com/w:411/h:91/q:mauto/f:best/https://entikmedia.com/wp-content/uploads/2023/12/cropped-cropped-logo_entik_media_web.jpg',
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];
  
  static isAuthenticated(): boolean {
    if (typeof window === "undefined") return false;
    return apiService.isAuthenticated();
  }
  
  static getCurrentUser(): User | null {
    return this.currentSession ? this.currentSession.user : null;
  }
  
  static getCurrentCompany(): Company | null {
    return this.currentSession ? this.currentSession.company : null;
  }
  
  static getSession(): Session | null {
    return this.currentSession;
  }
  
  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const apiRes: any = await apiService.login(credentials.email, credentials.password);
      if (apiRes.error || !apiRes.data?.token) {
        return {
          success: false,
          message: apiRes.message || apiRes.error || "Credenciales incorrectas",
          code: apiRes.code,
        };
      }
      const d = apiRes.data;
      apiService.setToken(d.token);

      const user: User = {
        id: d.user.id,
        email: d.user.email,
        name: d.user.username || d.user.email,
        role: d.user.role as UserRole,
        companyId: d.user.companyId || "",
        isActive: true,
        mustChangePassword: false,
        password: "",
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const company: Company = {
        id: d.user.companyId || "company_default",
        name: d.user.companyName || "Mi empresa",
        cif: "B00000000",
        address: "",
        city: "",
        postalCode: "",
        email: d.user.email,
        phone: "",
        website: "",
        isActive: true,
        invoicePrefix: "FAC",
        currentYear: new Date().getFullYear(),
        lastInvoiceNumber: 0,
        fiscalYearStart: 1,
        logo: "",
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const session: Session = {
        isAuthenticated: true,
        user,
        company,
        token: d.token,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000),
      };
      this.currentSession = session;

      return {
        success: true,
        message: d.message || "Login exitoso",
        session,
        user,
        company,
      };
    } catch {
      return {
        success: false,
        message: "Error de conexión con el servidor",
      };
    }
  }

  static async register(userData: any): Promise<AuthResponse> {
    return {
      success: false,
      message: 'Registro no disponible'
    };
  }
  
  static logout(): void {
    apiService.clearToken();
    this.currentSession = null;
    localStorage.removeItem(this.STORAGE_KEY);
  }
  
  static initializeDefaultUser(): void {
    // No hacer nada
  }
  
  static getUsers(): User[] {
    return this.users;
  }
  
  static validatePassword(password: string): PasswordValidation {
    if (password.length < 8) {
      return {
        isValid: false,
        message: 'La contraseña debe tener al menos 8 caracteres'
      };
    }
    
    if (!/(?=.*[a-z])/.test(password)) {
      return {
        isValid: false,
        message: 'La contraseña debe contener al menos una letra minúscula'
      };
    }
    
    if (!/(?=.*[A-Z])/.test(password)) {
      return {
        isValid: false,
        message: 'La contraseña debe contener al menos una letra mayúscula'
      };
    }
    
    if (!/(?=.*\d)/.test(password)) {
      return {
        isValid: false,
        message: 'La contraseña debe contener al menos un número'
      };
    }
    
    return {
      isValid: true,
      message: 'Contraseña válida'
    };
  }
  
  static async changePassword(userId: string, currentPassword: string, newPassword: string): Promise<AuthResponse> {
    const user = this.users.find(u => u.id === userId);
    if (!user || user.password !== currentPassword) {
      return {
        success: false,
        message: 'Contraseña actual incorrecta'
      };
    }
    
    user.password = newPassword;
    user.updatedAt = new Date();
    
    return {
      success: true,
      message: 'Contraseña cambiada exitosamente'
    };
  }
  
  static generatePassword(length: number = 12): string {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  }
  
  static async resetPassword(userId: string, newPassword: string): Promise<AuthResponse> {
    return {
      success: false,
      message: 'Reset de contraseña no disponible'
    };
  }
}
